import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, ViewChild, inject } from '@angular/core';
import { LoggerService } from '@app/shared/services/logger.service';

import { AvatarService } from '@shared/components/avatar/avatar.service';
import { NgStyle } from '@angular/common';

export class FileHolder {
  public pending: boolean = false;

  constructor(
    public src: string,
    public file: File
  ) {}
}

@Component({
  selector: 'avatar-upload',
  templateUrl: './avatar-upload.component.html',
  styleUrls: ['./avatar-upload.component.scss'],
  standalone: true,
  imports: [NgStyle],
})
export class AvatarUploadComponent implements OnChanges {
  private avatarService = inject(AvatarService);
  logger = inject(LoggerService);

  files: FileHolder[] = [];
  fileCounter: number = 0;
  showFileTooLargeMessage: boolean = false;
  supportedExtensions: string[] = ['image/x-png', 'image/gif', '.jpg', '.jpeg'];
  uploadedFiles: string[] | Array<{ url: string; fileName: string; blob?: Blob }> = [];

  @Input() maxFileSize: number;
  @Output() uploadStateChanged = new EventEmitter<boolean>();

  @ViewChild('input', { static: true })
  private inputElement: ElementRef;
  private pendingFilesCounter: number = 0;

  ngOnChanges(changes) {
    if (changes.uploadedFiles && changes.uploadedFiles.currentValue.length > 0) {
      this.processUploadedFiles();
    }
  }

  deleteFile(file: FileHolder): void {
    const index = this.files.indexOf(file);
    this.files.splice(index, 1);
    this.fileCounter--;
    if (this.inputElement) {
      this.inputElement.nativeElement.value = '';
    }
  }

  onFileChange(files: FileList) {
    const filesToUploadNum = files.length;

    if (filesToUploadNum != 0) {
      this.uploadStateChanged.emit(true);
    }

    this.fileCounter += filesToUploadNum;
    this.showFileTooLargeMessage = false;
    this.uploadFiles(files, filesToUploadNum);
  }

  private onResponse(fileHolder: FileHolder) {
    fileHolder.pending = false;

    if (--this.pendingFilesCounter == 0) {
      this.uploadStateChanged.emit(false);
    }
  }

  private processUploadedFiles() {
    for (let i = 0; i < this.uploadedFiles.length; i++) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const data: any = this.uploadedFiles[i];

      let fileBlob: Blob, file: File, fileUrl: string;

      if (data instanceof Object) {
        fileUrl = data.url;
        fileBlob = data.blob ? data.blob : new Blob([data]);
        file = new File([fileBlob], data.fileName);
      } else {
        fileUrl = data;
        fileBlob = new Blob([fileUrl]);
        file = new File([fileBlob], fileUrl);
      }

      this.files.push(new FileHolder(fileUrl, file));
    }
  }

  private async uploadFiles(files: FileList, filesToUploadNum: number) {
    for (let i = 0; i < filesToUploadNum; i++) {
      const file = files[i];

      if (this.maxFileSize && file.size > this.maxFileSize) {
        this.fileCounter--;
        this.inputElement.nativeElement.value = '';
        this.showFileTooLargeMessage = true;
        this.uploadStateChanged.emit(false);
        continue;
      }

      const img = document.createElement('img');
      img.src = window.URL.createObjectURL(file);

      const reader = new FileReader();
      reader.addEventListener(
        'load',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (event: any) => {
          const fileHolder: FileHolder = new FileHolder(event.target.result, file);
          this.uploadSingleFile(fileHolder);
          this.files.push(fileHolder);
        },
        false
      );
      reader.readAsDataURL(file);
    }
  }

  private uploadSingleFile(fileHolder: FileHolder) {
    this.pendingFilesCounter++;
    fileHolder.pending = true;

    //TODO (JOSE) unsubscribe
    this.avatarService.uploadAvatar(fileHolder.file).subscribe({
      next: () => this.onResponse(fileHolder),
      error: (error) => {
        this.logger.error(error);
        this.onResponse(fileHolder);
        this.deleteFile(fileHolder);
      },
    });
  }
}
